import { useEffect, useRef, useState } from 'react'
import {
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  X,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Send,
  Search,
  UserPlus,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { formatDate, formatCurrency } from '../../utils/helpers'

interface CartLine {
  product_id: string
  ref: string
  label: string
  quantity: number
  unit_price: number
  price_ttc: number
}

interface PendingOrder {
  id: number
  erp_id: number
  erp_name: string
  firstname: string
  lastname: string
  email: string
  phone: string | null
  company_name: string | null
  siren: string | null
  cart: CartLine[]
  status: 'pending' | 'processed' | 'cancelled'
  notes: string | null
  created_at: string
  updated_at: string
}

interface ErpCustomer {
  erp_customer_id: string
  customer_name: string
  city: string | null
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-700',
}

export default function AdminPendingOrders() {
  const { t } = useTranslation()

  const [orders, setOrders] = useState<PendingOrder[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('')
  const [selectedOrder, setSelectedOrder] = useState<PendingOrder | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
  const [statusConfirm, setStatusConfirm] = useState<{
    id: number
    status: 'processed' | 'cancelled'
  } | null>(null)
  const limit = 20

  // Process flow state
  const [processMode, setProcessMode] = useState<'existing' | 'new'>('existing')
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerResults, setCustomerResults] = useState<ErpCustomer[]>([])
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<ErpCustomer | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [processLoading, setProcessLoading] = useState(false)
  const [processError, setProcessError] = useState('')
  const [processResult, setProcessResult] = useState<{
    order_ref: string
    customer_created: boolean
    erp_customer_id: string
  } | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    load()
  }, [page, filterStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page, limit }
      if (filterStatus) params.status = filterStatus
      const res = await api.getAdminPendingOrders(params)
      setOrders(res.data)
      setTotal(res.total)
    } catch {
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const openDetail = (order: PendingOrder) => {
    setSelectedOrder(order)
    // Reset process flow
    setProcessMode('existing')
    setCustomerSearch('')
    setCustomerResults([])
    setSelectedCustomer(null)
    setNewCustomerName(order.company_name || `${order.firstname} ${order.lastname}`.trim())
    setProcessError('')
    setProcessResult(null)
    setShowDropdown(false)
  }

  const closeDetail = () => {
    setSelectedOrder(null)
    setProcessResult(null)
  }

  // Debounced ERP customer search
  const handleCustomerSearch = (q: string) => {
    setCustomerSearch(q)
    setSelectedCustomer(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (q.trim().length < 2) {
      setCustomerResults([])
      setShowDropdown(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      if (!selectedOrder) return
      setCustomerSearchLoading(true)
      try {
        const results = await api.searchErpCustomers(selectedOrder.erp_id, q.trim())
        setCustomerResults(results)
        setShowDropdown(true)
      } catch {
        setCustomerResults([])
      } finally {
        setCustomerSearchLoading(false)
      }
    }, 350)
  }

  const selectCustomer = (c: ErpCustomer) => {
    setSelectedCustomer(c)
    setCustomerSearch(c.customer_name)
    setShowDropdown(false)
    setProcessError('')
  }

  const handleProcess = async () => {
    if (!selectedOrder) return
    if (processMode === 'existing' && !selectedCustomer) return
    setProcessLoading(true)
    setProcessError('')
    try {
      const payload =
        processMode === 'new'
          ? { create_customer: true as const, customer_name: newCustomerName.trim() }
          : { erp_customer_id: selectedCustomer!.erp_customer_id }
      const res = await api.processAdminPendingOrder(selectedOrder.id, payload)
      setProcessResult({
        order_ref: res.order_ref,
        customer_created: res.customer_created,
        erp_customer_id: res.erp_customer_id,
      })
      // Refresh list and update selected order status
      await load()
      setSelectedOrder((o) => (o ? { ...o, status: 'processed' } : null))
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setProcessError(apiErr?.response?.data?.error || t('admin.pendingOrders.processError'))
    } finally {
      setProcessLoading(false)
    }
  }

  const updateStatus = async (id: number, status: string) => {
    setActionLoading(true)
    try {
      await api.updatePendingOrder(id, { status })
      await load()
      if (selectedOrder?.id === id) {
        setSelectedOrder((o) => (o ? { ...o, status: status as PendingOrder['status'] } : null))
      }
    } catch {
      /* ignore */
    } finally {
      setActionLoading(false)
    }
  }

  const saveNotes = async (id: number, notes: string) => {
    try {
      await api.updatePendingOrder(id, { notes })
    } catch {
      /* ignore */
    }
  }

  const handleDelete = async (id: number) => {
    setActionLoading(true)
    try {
      await api.deletePendingOrder(id)
      setDeleteConfirm(null)
      if (selectedOrder?.id === id) setSelectedOrder(null)
      await load()
    } catch {
      /* ignore */
    } finally {
      setActionLoading(false)
    }
  }

  const cartTotal = (cart: CartLine[]) => cart.reduce((s, l) => s + l.price_ttc, 0)

  const pages = Math.max(1, Math.ceil(total / limit))

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.pendingOrders.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('admin.pendingOrders.subtitle')}</p>
          </div>
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => {
              setPage(1)
              setFilterStatus(e.target.value)
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          >
            <option value="">{t('common.allStatuses')}</option>
            <option value="pending">{t('admin.pendingOrders.statusPending')}</option>
            <option value="processed">{t('admin.pendingOrders.statusProcessed')}</option>
            <option value="cancelled">{t('admin.pendingOrders.statusCancelled')}</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">{t('admin.pendingOrders.none')}</p>
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 sm:hidden">
                {orders.map((order) => (
                  <li key={order.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate text-sm">
                          {order.firstname} {order.lastname}
                        </p>
                        {order.company_name && (
                          <p className="text-xs text-gray-400 truncate">{order.company_name}</p>
                        )}
                        <p className="text-xs text-gray-500 truncate">{order.email}</p>
                      </div>
                      <span
                        className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[order.status] ?? 'bg-gray-100 text-gray-600'}`}
                      >
                        {order.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {order.status === 'processed' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {order.status === 'cancelled' && <XCircle className="w-3 h-3 mr-1" />}
                        {t(`admin.pendingOrders.status_${order.status}`)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <div className="min-w-0 text-xs text-gray-500 space-y-0.5">
                        <p className="truncate">
                          {order.erp_name || '—'} · {formatDate(order.created_at)}
                        </p>
                        <p className="font-semibold text-primary-700">
                          {formatCurrency(cartTotal(order.cart))}{' '}
                          <span className="font-normal text-gray-400">
                            ({order.cart.length} {t('admin.pendingOrders.items')})
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openDetail(order)}
                          title={t('common.viewDetails')}
                          className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {order.status === 'pending' && (
                          <>
                            <button
                              onClick={() =>
                                setStatusConfirm({ id: order.id, status: 'processed' })
                              }
                              disabled={actionLoading}
                              title={t('admin.pendingOrders.markProcessed')}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                setStatusConfirm({ id: order.id, status: 'cancelled' })
                              }
                              disabled={actionLoading}
                              title={t('admin.pendingOrders.markCancelled')}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(order.id)}
                          title={t('common.delete')}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">
                        {t('admin.pendingOrders.colDate')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">
                        {t('admin.pendingOrders.colCustomer')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">
                        {t('admin.pendingOrders.colErp')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">
                        {t('admin.pendingOrders.colTotal')}
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">
                        {t('common.status')}
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {order.firstname} {order.lastname}
                          </p>
                          <p className="text-xs text-gray-500">{order.email}</p>
                          {order.company_name && (
                            <p className="text-xs text-gray-400">{order.company_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{order.erp_name || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-primary-700">
                          {formatCurrency(cartTotal(order.cart))}
                          <span className="block text-xs font-normal text-gray-400">
                            {order.cart.length} {t('admin.pendingOrders.items')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[order.status] ?? 'bg-gray-100 text-gray-600'}`}
                          >
                            {order.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {order.status === 'processed' && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {order.status === 'cancelled' && <XCircle className="w-3 h-3 mr-1" />}
                            {t(`admin.pendingOrders.status_${order.status}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openDetail(order)}
                              title={t('common.viewDetails')}
                              className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {order.status === 'pending' && (
                              <>
                                <button
                                  onClick={() =>
                                    setStatusConfirm({ id: order.id, status: 'processed' })
                                  }
                                  disabled={actionLoading}
                                  title={t('admin.pendingOrders.markProcessed')}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    setStatusConfirm({ id: order.id, status: 'cancelled' })
                                  }
                                  disabled={actionLoading}
                                  title={t('admin.pendingOrders.markCancelled')}
                                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => setDeleteConfirm(order.id)}
                              title={t('common.delete')}
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {(page - 1) * limit + 1}–{Math.min(page * limit, total)} / {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  {page} / {pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail modal */}
        {selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('admin.pendingOrders.detailTitle')} #{selectedOrder.id}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
                <button onClick={closeDetail} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_BADGE[selectedOrder.status] ?? ''}`}
                  >
                    {selectedOrder.status === 'pending' && <Clock className="w-4 h-4" />}
                    {selectedOrder.status === 'processed' && <CheckCircle className="w-4 h-4" />}
                    {selectedOrder.status === 'cancelled' && <XCircle className="w-4 h-4" />}
                    {t(`admin.pendingOrders.status_${selectedOrder.status}`)}
                  </span>
                  {selectedOrder.status === 'pending' && !processResult && (
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setStatusConfirm({ id: selectedOrder.id, status: 'processed' })
                        }
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {t('admin.pendingOrders.markProcessed')}
                      </button>
                      <button
                        onClick={() =>
                          setStatusConfirm({ id: selectedOrder.id, status: 'cancelled' })
                        }
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        {t('admin.pendingOrders.markCancelled')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Customer info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    {t('admin.pendingOrders.sectionCustomer')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">{t('admin.pendingOrders.fieldName')}</span>
                      <p className="font-medium">
                        {selectedOrder.firstname} {selectedOrder.lastname}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('admin.pendingOrders.fieldEmail')}</span>
                      <p className="font-medium break-all">{selectedOrder.email}</p>
                    </div>
                    {selectedOrder.phone && (
                      <div>
                        <span className="text-gray-500">{t('admin.pendingOrders.fieldPhone')}</span>
                        <p className="font-medium">{selectedOrder.phone}</p>
                      </div>
                    )}
                    {selectedOrder.company_name && (
                      <div>
                        <span className="text-gray-500">
                          {t('admin.pendingOrders.fieldCompany')}
                        </span>
                        <p className="font-medium">{selectedOrder.company_name}</p>
                      </div>
                    )}
                    {selectedOrder.siren && (
                      <div>
                        <span className="text-gray-500">SIREN</span>
                        <p className="font-medium">{selectedOrder.siren}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">{t('admin.pendingOrders.fieldErp')}</span>
                      <p className="font-medium">{selectedOrder.erp_name || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Cart */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    {t('admin.pendingOrders.sectionCart')}
                  </h3>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          {t('admin.pendingOrders.colArticle')}
                        </th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">
                          {t('admin.pendingOrders.colQty')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">
                          {t('admin.pendingOrders.colPriceTtc')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.cart.map((line, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{line.label}</p>
                            {line.ref && <p className="text-xs text-gray-400">{line.ref}</p>}
                          </td>
                          <td className="px-3 py-2 text-center text-gray-700">{line.quantity}</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-700">
                            {formatCurrency(line.price_ttc)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200">
                        <td colSpan={2} className="px-3 py-2 text-right font-bold text-gray-800">
                          {t('cart.totalInclTax')}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-primary-700">
                          {formatCurrency(cartTotal(selectedOrder.cart))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* ERP process section — only for pending orders */}
                {selectedOrder.status === 'pending' && (
                  <div className="border border-primary-200 rounded-xl p-4 bg-primary-50 space-y-4">
                    <h3 className="text-sm font-semibold text-primary-800 uppercase tracking-wide flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {t('admin.pendingOrders.sectionProcess')}
                    </h3>

                    {processResult ? (
                      // Success state
                      <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="font-semibold text-green-800">
                            {t('admin.pendingOrders.processSuccess')}
                          </p>
                          {processResult.order_ref && (
                            <p className="text-sm text-green-700">
                              {t('admin.pendingOrders.processRef')}{' '}
                              <span className="font-mono font-bold">{processResult.order_ref}</span>
                            </p>
                          )}
                          {processResult.customer_created && (
                            <p className="text-sm text-green-700">
                              {t('admin.pendingOrders.processCustomerCreated')}{' '}
                              <span className="font-mono font-bold">
                                #{processResult.erp_customer_id}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Mode toggle */}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setProcessMode('existing')
                              setProcessError('')
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                              processMode === 'existing'
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <Search className="w-3.5 h-3.5" />
                            {t('admin.pendingOrders.modeExisting')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setProcessMode('new')
                              setProcessError('')
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                              processMode === 'new'
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            {t('admin.pendingOrders.modeNew')}
                          </button>
                        </div>

                        {/* Existing customer search */}
                        {processMode === 'existing' && (
                          <>
                            <p className="text-sm text-primary-700">
                              {t('admin.pendingOrders.processHint')}
                            </p>
                            <div className="relative" ref={searchRef}>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('admin.pendingOrders.processSelectCustomer')}
                              </label>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                <input
                                  type="text"
                                  value={customerSearch}
                                  onChange={(e) => handleCustomerSearch(e.target.value)}
                                  onFocus={() =>
                                    customerResults.length > 0 && setShowDropdown(true)
                                  }
                                  placeholder={t('admin.pendingOrders.processSearchPlaceholder')}
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                                {customerSearchLoading && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                                  </div>
                                )}
                              </div>

                              {/* Dropdown */}
                              {showDropdown && customerResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                  {customerResults.map((c) => (
                                    <button
                                      key={c.erp_customer_id}
                                      type="button"
                                      onClick={() => selectCustomer(c)}
                                      className="w-full text-left px-4 py-2.5 hover:bg-primary-50 transition-colors text-sm border-b border-gray-100 last:border-0"
                                    >
                                      <span className="font-medium text-gray-900">
                                        {c.customer_name}
                                      </span>
                                      {c.city && (
                                        <span className="text-gray-400 ml-2 text-xs">{c.city}</span>
                                      )}
                                      <span className="block text-xs text-gray-400 font-mono">
                                        {c.erp_customer_id}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Selected customer badge */}
                            {selectedCustomer && (
                              <div className="flex items-center gap-2 text-sm bg-white border border-primary-200 rounded-lg px-3 py-2">
                                <CheckCircle className="w-4 h-4 text-primary-600 flex-shrink-0" />
                                <span className="text-gray-700">
                                  <span className="font-semibold">
                                    {selectedCustomer.customer_name}
                                  </span>
                                  <span className="text-gray-400 ml-2 font-mono text-xs">
                                    #{selectedCustomer.erp_customer_id}
                                  </span>
                                </span>
                              </div>
                            )}
                          </>
                        )}

                        {/* New customer creation */}
                        {processMode === 'new' && (
                          <>
                            <p className="text-sm text-primary-700">
                              {t('admin.pendingOrders.processNewHint')}
                            </p>
                            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 text-sm">
                              {/* Editable name */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  {t('admin.pendingOrders.processNewName')}
                                </label>
                                <input
                                  type="text"
                                  value={newCustomerName}
                                  onChange={(e) => setNewCustomerName(e.target.value)}
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                              </div>
                              {/* Read-only contact info from pending order */}
                              <div className="grid grid-cols-2 gap-2 text-gray-500">
                                <div>
                                  <span className="text-xs font-medium text-gray-400 uppercase">
                                    {t('admin.pendingOrders.fieldEmail')}
                                  </span>
                                  <p className="text-gray-700">{selectedOrder.email}</p>
                                </div>
                                {selectedOrder.phone && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-400 uppercase">
                                      {t('admin.pendingOrders.fieldPhone')}
                                    </span>
                                    <p className="text-gray-700">{selectedOrder.phone}</p>
                                  </div>
                                )}
                                {selectedOrder.siren && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-400 uppercase">
                                      SIREN
                                    </span>
                                    <p className="text-gray-700">{selectedOrder.siren}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {/* Error */}
                        {processError && (
                          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {processError}
                          </p>
                        )}

                        {/* Process button */}
                        <button
                          onClick={handleProcess}
                          disabled={
                            (processMode === 'existing' && !selectedCustomer) ||
                            (processMode === 'new' && !newCustomerName.trim()) ||
                            processLoading
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {processLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          ) : processMode === 'new' ? (
                            <UserPlus className="w-4 h-4" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          {processLoading
                            ? t('admin.pendingOrders.processSending')
                            : processMode === 'new'
                              ? t('admin.pendingOrders.processSubmitNew')
                              : t('admin.pendingOrders.processSubmit')}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    {t('admin.pendingOrders.fieldNotes')}
                  </label>
                  <textarea
                    defaultValue={selectedOrder.notes ?? ''}
                    onBlur={(e) => saveNotes(selectedOrder.id, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
                    placeholder={t('admin.pendingOrders.notesPlaceholder')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status confirm modal */}
      {statusConfirm !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-fade-in">
            {/* Colored header strip */}
            <div
              className={`h-1.5 w-full ${statusConfirm.status === 'processed' ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-gray-400 to-gray-600'}`}
            />
            <div className="p-6 space-y-4">
              {/* Icon + title */}
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${statusConfirm.status === 'processed' ? 'bg-green-100' : 'bg-gray-100'}`}
                >
                  {statusConfirm.status === 'processed' ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {t(
                      statusConfirm.status === 'processed'
                        ? 'admin.pendingOrders.confirmProcessedTitle'
                        : 'admin.pendingOrders.confirmCancelledTitle'
                    )}
                  </h3>
                </div>
              </div>
              {/* Message */}
              <p className="text-sm text-gray-500">
                {t(
                  statusConfirm.status === 'processed'
                    ? 'admin.pendingOrders.confirmProcessedMessage'
                    : 'admin.pendingOrders.confirmCancelledMessage'
                )}
              </p>
              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => {
                    updateStatus(statusConfirm.id, statusConfirm.status)
                    setStatusConfirm(null)
                  }}
                  disabled={actionLoading}
                  className={`flex-1 py-2.5 rounded-xl font-medium text-sm text-white transition-colors disabled:opacity-50 ${statusConfirm.status === 'processed' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600'}`}
                >
                  {t(
                    statusConfirm.status === 'processed'
                      ? 'admin.pendingOrders.confirmProcessedBtn'
                      : 'admin.pendingOrders.confirmCancelledBtn'
                  )}
                </button>
                <button
                  onClick={() => setStatusConfirm(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm space-y-4">
            <p className="font-semibold text-gray-900">{t('admin.pendingOrders.deleteConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                {t('common.delete')}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
